import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, RefreshCw } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ErrorStateProps {
  title: string;
  /**
   * What actually went wrong. Pass the server's message where there is one —
   * "Something went wrong" alone gives the user nothing to act on or report.
   */
  description?: string;
  /**
   * Wired to the query's `refetch`. Omit only when there is genuinely nothing to
   * retry; a fetch that failed can always be tried again, and without this the
   * user's only recourse is a full page reload.
   */
  onRetry?: () => void | Promise<unknown>;
  retryLabel?: string;
  className?: string;
}

/**
 * The failure counterpart to `EmptyState`: shown in place of the content a
 * failed query would have filled.
 *
 * Deliberately an `Alert variant="destructive"` rather than the centred
 * treatment `EmptyState` uses. An empty list is a normal state and reads as
 * page content; a failed load is an interruption, and the boxed, role="alert"
 * form both announces itself to screen readers and stays visually distinct from
 * "there is nothing here".
 */
export function ErrorState({
  title,
  description,
  onRetry,
  retryLabel,
  className,
}: ErrorStateProps): React.ReactElement {
  const { t } = useTranslation();
  /*
    Whether a retry is in flight.

    Without this the button was the only control in the app that gave no sign it had
    been pressed: on a failing request the whole point is that nothing on screen
    changes, so "Try again" looked like it had not registered and got clicked three or
    four more times — each one a fresh request against a server already in trouble.

    Driven by the promise `onRetry` returns rather than a prop, so a call site only has
    to hand over `refetch` (which resolves when the refetch settles) instead of also
    threading `isRefetching` down. A call site that returns nothing keeps the old
    behaviour rather than sticking in a spinner that never stops.
  */
  const [retrying, setRetrying] = useState(false);
  const retry = () => {
    if (!onRetry) return;
    const result = onRetry();
    if (!result || typeof (result as Promise<unknown>).then !== "function") return;
    setRetrying(true);
    void (result as Promise<unknown>).finally(() => setRetrying(false));
  };

  return (
    <Alert variant="destructive" className={cn("max-w-2xl", className)}>
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      {description ? <AlertDescription>{description}</AlertDescription> : null}
      {onRetry ? (
        <Button
          variant="outline"
          size="sm"
          onClick={retry}
          disabled={retrying}
          className="mt-3 w-fit"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", retrying && "animate-spin")} />
          {retrying
            ? t("common.retrying", "Trying again…")
            : (retryLabel ?? t("common.tryAgain", "Try again"))}
        </Button>
      ) : null}
    </Alert>
  );
}
